import { SensorData } from '../types';

export const formatSensorValue = (sensor: SensorData): string => {
  if (!sensor.current_value?.value) return 'N/A';
  
  const value = sensor.current_value.value;
  const unit = sensor.display_config?.unit || '';
  
  switch (sensor.type) {
    case 'fuel':
      return `${Math.round(value)}${unit ? ` ${unit}` : '%'}`;
    case 'temperature':
      return `${Math.round(value)}${unit || '°C'}`;
    case 'pressure':
      return `${value.toFixed(1)}${unit || ' bar'}`;
    case 'speed':
      return `${Math.round(value)}${unit || ' km/h'}`;
    case 'engine_hours':
      return `${Math.round(value)}${unit || ' hrs'}`;
    case 'vibration':
      return `${value.toFixed(2)}${unit || ' g'}`;
    default:
      return `${value}${unit ? ` ${unit}` : ''}`;
  }
};

export const getSensorIcon = (type: SensorData['type']): string => {
  const icons = {
    fuel: '⛽',
    temperature: '🌡️',
    pressure: '📊',
    vibration: '📳',
    speed: '🚗',
    gps: '📍',
    engine_hours: '⚙️',
    other: '📡',
  };
  return icons[type] || '📡';
};

export const getSensorDisplayName = (type: SensorData['type']): string => {
  const names = {
    fuel: 'Fuel Level',
    temperature: 'Temperature',
    pressure: 'Pressure',
    vibration: 'Vibration',
    speed: 'Speed',
    gps: 'GPS Location',
    engine_hours: 'Generator',
    other: 'Other',
  };
  return names[type] || 'Unknown';
};

export const getSensorColor = (type: SensorData['type']): string => {
  const colors = {
    fuel: 'bg-blue-500',
    temperature: 'bg-orange-500',
    pressure: 'bg-purple-500',
    vibration: 'bg-yellow-500',
    speed: 'bg-green-500',
    gps: 'bg-indigo-500',
    engine_hours: 'bg-cyan-500',
    other: 'bg-gray-500',
  };
  return colors[type] || 'bg-gray-500';
};

export const getStatusColor = (isOnline: boolean): string => {
  return isOnline ? 'text-green-500' : 'text-red-500';
};

export const formatTimestamp = (timestamp: string | null): string => {
  if (!timestamp) return 'Never';
  
  let date: Date;
  
  // Handle the specific format "14-09-2025 04:06:11 PM" for backward compatibility
  if (timestamp.match(/\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2} (AM|PM)/)) {
    try {
      // Parse the custom format: DD-MM-YYYY HH:mm:ss AM/PM
      const [datePart, timePart, period] = timestamp.split(' ');
      const [day, month, year] = datePart.split('-');
      const [hour, minute, second] = timePart.split(':');
      
      // Convert to 24-hour format
      let hour24 = parseInt(hour);
      if (period === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (period === 'AM' && hour24 === 12) {
        hour24 = 0;
      }
      
      // Create date object using local timezone
      // Note: month is 0-indexed in JavaScript Date constructor
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), hour24, parseInt(minute), parseInt(second));
    } catch (e) {
      console.warn('Failed to parse custom timestamp format:', timestamp, e);
      return 'Invalid Date';
    }
  } else {
    // Try standard date parsing (ISO strings, etc.)
    date = new Date(timestamp);
  }
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    console.warn('Invalid timestamp provided:', timestamp);
    return 'Invalid Date';
  }
  
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  
  return date.toLocaleDateString();
};

export const calculateFuelPercentage = (value: number, min: number = 0, max: number = 100): number => {
  return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
};

export const formatSafeTimestamp = (date: Date | null): string => {
  if (!date || isNaN(date.getTime())) {
    return 'Never';
  }
  return date.toLocaleTimeString();
};

export const getEngineHoursStatus = (hours: number): { status: string; color: string } => {
  if (hours < 100) return { status: 'New', color: 'text-green-600' };
  if (hours < 500) return { status: 'Good', color: 'text-blue-600' };
  if (hours < 1000) return { status: 'Moderate', color: 'text-yellow-600' };
  if (hours < 2000) return { status: 'High', color: 'text-orange-600' };
  return { status: 'Critical', color: 'text-red-600' };
};